export function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element #${id}`);
  return node as T;
}

type Child = Node | string | null | undefined | false;

/** Builds an element. Text goes through textContent, never innerHTML. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/** The skip link's default jump would replace location.hash (the link secret), so focus #main instead. */
export function wireSkipLink(): void {
  document.querySelector<HTMLAnchorElement>('[data-skip-link]')?.addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('main')?.focus();
  });
}

let liveRegion: HTMLElement | null = null;

function announce(text: string): void {
  if (!liveRegion) {
    liveRegion = el('div', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });
    document.body.append(liveRegion);
  }
  liveRegion.textContent = text;
}

async function copyText(source: HTMLElement): Promise<void> {
  const text = 'value' in source ? String((source as HTMLInputElement).value) : (source.textContent ?? '');
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    if ('select' in source) {
      (source as HTMLInputElement).select();
    } else {
      const range = document.createRange();
      range.selectNodeContents(source);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    document.execCommand('copy');
  }
}

/** Wires every `[data-copy="<element id>"]` button under root. */
export function wireCopyButtons(root: ParentNode = document): void {
  root.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach((btn) => {
    const label = btn.querySelector<HTMLElement>('.btn-label') ?? btn;
    const original = label.textContent ?? 'Copy';
    let timer: number | undefined;
    btn.addEventListener('click', async () => {
      const source = document.getElementById(btn.dataset.copy ?? '');
      if (!source) return;
      await copyText(source);
      label.textContent = 'Copied';
      announce('Copied to clipboard');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        label.textContent = original;
      }, 1500);
    });
  });
}

const ICONS = {
  info: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'M12 11v5', 'M12 8h.01'],
  check: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'm8.5 12.5 2.5 2.5 4.5-5'],
  alert: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z', 'M12 8v5', 'M12 16h.01'],
} as const;

/** Inline Lucide-style SVG built with the DOM (no innerHTML). */
export function icon(name: keyof typeof ICONS, size = 16): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', 'icon');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of ICONS[name]) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}
